import { useEffect, useState } from "react";
import uniq from "lodash/uniq";

import { fetchWithAuth } from "../../../../../../api-client/APIClient";
import { HookEvent } from "../enums";

export interface CategoryMap {
  [key: string]: string;
}

export interface ModelToFieldsMap {
  [key: string]: string[];
}

export interface CategoryToModelsToFieldsMap {
  [key: string]: ModelToFieldsMap;
}

export interface WebhookEvents {
  webhook_events: Array<string>;
  field_data: Record<string, Array<string>>;
  enum_origin_field_data?: Record<string, Array<string>>;
}

const toCategoryToModelsToFieldsMap = (
  fieldData: Record<string, Array<string>>,
): CategoryToModelsToFieldsMap =>
  Object.keys(fieldData).reduce(
    (accumulatingObj: CategoryToModelsToFieldsMap, categoryModel: string) => {
      const [category, model] = categoryModel.split(".");
      if (!accumulatingObj[category]) {
        accumulatingObj[category] = {};
      }
      accumulatingObj[category][model] = fieldData[categoryModel];
      return accumulatingObj;
    },
    {},
  );

export const useWebhookOptions = () => {
  // state
  const [isWebhookOptionsLoading, setIsWebhookOptionsLoading] = useState(true);
  const [commonModelsOptions, setCommonModelsOptions] = useState<Array<string>>([]);
  const [modelToCategoryMap, setModelToCategoryMap] = useState<CategoryMap>({});
  const [isIssuesWebhookEnabled, setIsIssuesWebhookEnabled] = useState<boolean>(false);
  const [isIssuesMutedEnabled, setIsIssuesMutedEnabled] = useState<boolean>(false);
  const [modelsToFieldsEnabled, setModelsToFieldsEnabled] = useState<CategoryToModelsToFieldsMap>(
    {},
  );
  const [enumFieldsEnabled, setEnumFieldsEnabled] = useState<CategoryToModelsToFieldsMap>({});
  const [modelsToFieldsSelected, setModelsToFieldsSelected] = useState<Record<string, string[]>>(
    {},
  );

  // event handlers
  const fetchOptions = () => {
    fetchWithAuth({
      path: "/integrations/configuration/webhooks/events",
      method: "GET",
      onResponse: (data: WebhookEvents) => {
        if (
          data.webhook_events.includes(HookEvent.ISSUES_CREATED) &&
          data.webhook_events.includes(HookEvent.ISSUES_RESOLVED)
        ) {
          setIsIssuesWebhookEnabled(true);
        }

        // Track Issue.muted separately from the card gate. The card stays available
        // on created + resolved (so an existing issues webhook is always visible and
        // editable), and muted is only added to the submitted events when the backend
        // advertises it. This keeps a backend that hasn't shipped Issue.muted from
        // rejecting the save.
        if (data.webhook_events.includes(HookEvent.ISSUES_MUTED)) {
          setIsIssuesMutedEnabled(true);
        }

        data.webhook_events = uniq(
          data.webhook_events
            // split length of 3: category.model.event
            .filter((commonModel: string) => commonModel.split(".").length === 3)
            // remove the event field (leave category.model pairing)
            .map(
              (commonModel: string) => `${commonModel.split(".")[0]}.${commonModel.split(".")[1]}`,
            )
            .sort(),
        );

        // create a map so the typeahead can display category given just the model map{cat:model}
        setModelToCategoryMap(
          data.webhook_events.reduce((accumulatingObj: CategoryMap, categoryModel: string) => {
            return {
              ...accumulatingObj,
              [categoryModel.split(".")[1]]: categoryModel.split(".")[0].toLowerCase(),
            };
          }, {}),
        );

        setModelsToFieldsEnabled(toCategoryToModelsToFieldsMap(data.field_data));
        setEnumFieldsEnabled(toCategoryToModelsToFieldsMap(data.enum_origin_field_data ?? {}));

        // options array lists all the Common Models
        setCommonModelsOptions(
          data.webhook_events.map((commonModel: string) => {
            return `${commonModel.split(".")[1]}`;
          }),
        );
        setIsWebhookOptionsLoading(false);
      },
    });
  };

  // effects
  // initialize
  useEffect(() => {
    fetchOptions();
  }, []);

  return {
    isWebhookOptionsLoading,
    commonModelsOptions,
    modelToCategoryMap,
    isIssuesWebhookEnabled,
    isIssuesMutedEnabled,
    modelsToFieldsEnabled,
    enumFieldsEnabled,
  };
};
